import React from 'react'

import { SparklesIcon, WrenchIcon, BugIcon, ShieldCheckIcon, ArchiveXIcon } from 'lucide-react'

import { Stack, Typography, useTheme } from '@mui/material'

import { useColorMode } from 'hooks'
import { SUPPORT_REQUEST_TYPES } from 'constants/statuses'
// Sibling paths, not the `components` barrel: this file is exported FROM that barrel,
// so going through it would be a cycle. Same reason `SupportTypeBadge` and
// `AnnouncementCard` state.
import { STATUS_TOKEN_H, statusTone } from '../StatusChip/StatusChip'
import type { SupportRequestTypeKey } from '../SupportTypeBadge/SupportTypeBadge'
import { ANNOUNCEMENT_KIND_LABELS } from '@repo/types'
import type { AnnouncementKind } from 'services/announcementsApi'

/**
 * ONE badge for an announcement's KIND, rendered identically everywhere it appears (§36).
 *
 * ## Why this was extracted (VRG-332)
 *
 * It lived inside `AnnouncementCard` — the icon table, the tone table, and the
 * support-counterpart colour resolution, all private to that file — and the internal
 * authoring list rendered the kind as BARE TEXT ("feature", lowercase, the raw enum
 * value). Putting a second copy of the tone mapping into that table was the obvious
 * move and is exactly the drift §36 exists to prevent: two colour tables for one
 * concept, agreeing today, diverging the first time a kind is added or a hue is tuned.
 * `security` and `deprecation` were added late for precisely that kind of reason, and
 * that change already had to touch three separate copies of the label map.
 *
 * So the card and the table now render this component. The card's chip is unchanged —
 * same glyphs, same tones, same 28px footprint — it just no longer owns them.
 *
 * ## Why kind is a BADGE while status stays a PILL
 *
 * Same distinction `SupportTypeBadge` documents for a request's type, and it applies
 * here for the same reason. Status is a LIFECYCLE state — draft → published, something
 * the record moves through — which is what the pill means everywhere else in the app.
 * Kind is a fixed CLASSIFICATION chosen when the announcement is written and never
 * changed after. Giving both the pill treatment made two chips in one row read as two
 * states of one thing.
 */

const KIND_ICON: Record<AnnouncementKind, React.ReactNode> = {
  feature: <SparklesIcon size={14} strokeWidth={1.5} />,
  improvement: <WrenchIcon size={14} strokeWidth={1.5} />,
  fix: <BugIcon size={14} strokeWidth={1.5} />,
  security: <ShieldCheckIcon size={14} strokeWidth={1.5} />,
  deprecation: <ArchiveXIcon size={14} strokeWidth={1.5} />,
}

/**
 * The chip's tone for every kind WITHOUT a support counterpart — `feature` and `fix`
 * are resolved from the support table instead (see KIND_SUPPORT_COUNTERPART below).
 * Their entries here are the fallback if that mapping is ever narrowed.
 *
 * This is the only reason `security` and `deprecation` are worth having as separate
 * kinds at all. `improvement` is "something got better", so it keeps the brand tint.
 * The other two are not that: a deprecation REQUIRES the reader to act, and rendering
 * it in the same purple as a shipped feature tells them it is optional reading.
 * Warning tone is the signal that the announcement has a deadline attached; security
 * gets `info` — distinct from the routine kinds so it is findable later, without the
 * alarm of warning, since a shipped security note is usually reassurance rather than
 * a task.
 *
 * Returns a palette KEY rather than a colour so both halves (text and the fill) stay
 * derived from one choice.
 */
const KIND_TONE: Record<AnnouncementKind, 'brand' | 'warning' | 'info'> = {
  feature: 'brand',
  improvement: 'brand',
  fix: 'brand',
  security: 'info',
  deprecation: 'warning',
}

/**
 * The two announcement kinds that ARE a support request type, and which one.
 *
 * An announcement usually exists because a request closed — the card's own "You asked
 * for these" row links the very board items it shipped. So where a counterpart exists,
 * the two chips read their colour from ONE table (`SUPPORT_REQUEST_TYPES`, via the same
 * `statusTone` the support badge uses) rather than from two similar-looking hues that
 * happen to agree today. `brand[600]` is `hsl(271 81% 56%)` and support's feature purple
 * is `hsl(271 72% 47%)` — same family, visibly different, and nothing would have caught
 * the drift.
 *
 * Only TWO of the five map. `improvement`, `security` and `deprecation` have no request
 * a user could have filed, and inventing counterparts for them would be parity in name
 * only — so they keep their own tone and fall through to KIND_TONE above.
 *
 * `fix` → `bug` is a colour link, not a naming one: the announcement stays "Fixed"
 * (what shipped), while the red ties it back to the bug the reader reported.
 */
const KIND_SUPPORT_COUNTERPART: Partial<Record<AnnouncementKind, SupportRequestTypeKey>> = {
  feature: 'feature',
  fix: 'bug',
}

interface AnnouncementKindBadgeProps {
  kind: AnnouncementKind
}

export const AnnouncementKindBadge = ({ kind }: AnnouncementKindBadgeProps) => {
  const theme = useTheme()
  const { resolvedMode } = useColorMode()
  const isDark = resolvedMode === 'dark'

  const label = ANNOUNCEMENT_KIND_LABELS[kind]
  if (!label) {
    // A kind the client does not know about — a row written by a newer API than this
    // bundle. Rendering an unstyled chip (the failure the closed union exists to
    // prevent) would be worse than rendering nothing, and the console line is what
    // makes the mismatch findable. Mirrors `SupportTypeBadge`'s guard.
    console.error(`Invalid announcement 'kind': ${kind}`)
    return null
  }

  /**
   * ALL THREE PARTS from `statusTone`, not just the text colour.
   *
   * The chip used to take `statusTone(...).color` and hand-roll a fill from it, with no
   * border at all — which is why it read as a flat block beside the Status pills in the
   * same row. `statusTone` already derives text, border and fill from ONE hue, per mode:
   *
   *   dark   color lighten(hue,.4)  border alpha(hue,.4)   bg alpha(hue,.18)
   *   light  color hue              border borderColor??hue bg backgroundColor??alpha(hue,.14)
   *
   * Taking all three is the only way the badge and the pill cannot drift per mode. The
   * split that makes hand-derivation fail is that the AUTHORED hue is the light-mode TEXT
   * colour — reuse it raw for a dark-mode fill and you get dark-on-dark.
   *
   * WHAT IS NOT SHARED IS THE SHAPE. Pills are fully rounded (999px); this stays a
   * rectangle at the 6px radius. The silhouette is what separates a fixed CLASSIFICATION
   * from a LIFECYCLE state, and that distinction is the whole reason this component and
   * `StatusChip` are two things — so only the colour treatment is shared, never the form.
   *
   * `feature` and `fix` resolve their hue through `SUPPORT_REQUEST_TYPES`, which carries
   * authored light-mode pastels (`backgroundColor`/`borderColor`); the other three have
   * only a hue, so `statusTone` derives the pair. Both go through the same function, so
   * both get whatever that function decides.
   */
  const tone = KIND_TONE[kind]
  const counterpart = KIND_SUPPORT_COUNTERPART[kind]
  const source = counterpart
    ? SUPPORT_REQUEST_TYPES[counterpart]
    : { color: tone === 'brand' ? theme.palette.brand[600] : theme.palette[tone].main }
  const { color: toneColor, border: toneBorder, background: toneFill } = statusTone(
    source,
    isDark,
  )

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        /*
          SIZED TO MATCH `SupportTypeBadge` and the status pill, so a type badge on a
          support request, a kind chip on an announcement and a Draft/Published pill are
          the same object at the same size. They already shared a 6px radius and a
          14px/600 label; only the height disagreed — `py: 1` gave 22px against the
          support badge's `minHeight: 28`. Two badges one size apart, four pixels, across
          two surfaces a user moves between via the Help menu.

          `minHeight` rather than `py`, copied from that component rather than
          re-derived: padding plus a line box is an indirect way to state a height, and
          it drifts the moment the label's line-height changes.
        */
        px: 2,
        minHeight: STATUS_TOKEN_H,
        // 6px, NOT the pill's 999px. See the tone block above: the badge shares the
        // status token's colour treatment and deliberately not its silhouette.
        borderRadius: 1.5,
        flexShrink: 0,
        width: 'fit-content',
        color: toneColor,
        // 1px in every state, so nothing this badge does can shift the row it sits in.
        border: `1px solid ${toneBorder}`,
        bgcolor: toneFill,
      }}
    >
      {KIND_ICON[kind]}
      {/*
        12px / 500 — the STATUS PILL's type, not `body3`'s 14/600 (`StatusChip.tsx`
        sets `fontSize: isSmall ? 12 : 14` and `fontWeight: isSmall ? 500 : 400`).
        Height already agreed via the shared `STATUS_TOKEN_H`, so this was the last
        axis on which a badge and the pill beside it in the same table row still
        disagreed.

        WEIGHT MATCHED TOO, at 500, rather than keeping 600 as a "classification vs
        state" cue. With the border, the fill, the height and now the size all shared,
        a lone weight difference reads as an inconsistency rather than a signal — and
        the distinction it would be carrying is already carried, far more legibly, by
        the SILHOUETTE: 6px rectangle against a 999px pill. One weak signal duplicating
        a strong one is not worth the appearance of a mistake.
      */}
      <Typography sx={{ fontSize: 12, fontWeight: 500, lineHeight: 1 }}>
        {label}
      </Typography>
    </Stack>
  )
}
